import { Component, Inject, Injector } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { CTMapFiltrableProperties } from '@cloud-twin-v2/ct-map/models/ct-map-filtrable-properties';
import { CTMapBuilderService } from '@cloud-twin-v2/ct-map/services/ct-map-builder.service';
import { CTMapPersistencyService } from '@cloud-twin-v2/ct-map/services/ct-map-persistency.service';
import { FrontendSettingsService } from '@cloud-twin-v2/generated/services/frontend-settings.service';
import {
  FrontendSettingsMapDto,
  WorkspaceConnectSettingDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { ScenarioItem } from '@cloud-twin-v2/scenarios-configuration/scenario-item';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { SetValueAction } from '@common-modules/dynamic-layout/state/generic/generic.actions';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { GenericMapSettings } from '@common-modules/wlm-map/generic-map/models/generic-map-settings';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { LayerNameSpace } from '@common-modules/wlm-map/models/layer-namespace';
import { ReplaySubject } from 'rxjs';

export const MAP_WIDGET_COMPONENT_INSTANCE = `CtMapWidgetComponent#1`;
@Component({
  selector: 'ct-map-widget',
  templateUrl: './ct-map-widget.component.html',
  styleUrl: './ct-map-widget.component.scss',
  standalone: false,
  providers: [ReduxStateService],
})
export class CtMapWidgetComponent extends BaseDynamicWidgetComponent {
  widgetInstanceKey: string = 'A';
  parentWidgetInstanceKey: string = 'B';
  namespaces: LayerNameSpace[] = [];
  connectSettings: WorkspaceConnectSettingDto[];
  singleId: string = '1';
  tooltipMetadata = [];
  mapSettings: GenericMapSettings;
  scenarioVisibilityChange$ = new ReplaySubject<ScenarioItem>(1);
  namespacesChange$ = new ReplaySubject<Partial<CTMapFiltrableProperties>>(1);
  namespacesChange: Partial<CTMapFiltrableProperties>;
  mapFrontendSettings: FrontendSettingsMapDto;

  private readonly T_SCOPE = TranslateScopes.CtMap;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private readonly _selectedWorkspaceService: SelectedWorkspaceService,
    private readonly _ctMapPersistencyService: CTMapPersistencyService,
    private readonly _ctMapBuilderService: CTMapBuilderService,
    private readonly _frontendSettingsServices: FrontendSettingsService,
  ) {
    super(injector, widgetSettings);
  }

  get componentName(): string {
    return 'CtMapWidgetComponent';
  }

  onWidgetInit(): void {
    this._selectedWorkspaceService.selectedWorkspace$.subscribe((workspace) => {
      if (!workspace) {
        return;
      }
      this._ctMapBuilderService
        .buildMapSettings({
          scenarioVisibilityChange$: this.scenarioVisibilityChange$,
          namespacesChange$: this.namespacesChange$,
        })
        .subscribe((settings) => {
          setTimeout(() => {
            this.mapSettings = settings;
            if (settings) {
              this.onLayersVisibilityChange(this.mapSettings.layerItems);
            }
          });
        });

      this.scenarioVisibilityChange$.subscribe((scenarioInfo: ScenarioItem) => {
        this.dispatchScenarioVisibilityChange(scenarioInfo);
      });

      this.namespacesChange$.subscribe((namespaces: Partial<CTMapFiltrableProperties>) => {
        this.namespacesChange = namespaces;
      });
    });

    this._frontendSettingsServices.getFrontendSettings().subscribe((settings) => {
      this.mapFrontendSettings = settings.map;
    });
  }

  dispatchScenarioVisibilityChange(scenarioInfo: ScenarioItem) {
    this._ctMapPersistencyService.persistScenarioVisibility(
      scenarioInfo.scenarioId,
      scenarioInfo.isVisible,
    );

    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.ScenarioVisibility,
        value: scenarioInfo,
      }),
    );
  }

  onTimeStepChange(timeStep: Date): void {
    this.dispatchTimeStepChange(timeStep);
  }

  dispatchTimeStepChange(timeStep: Date) {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.MapStep,
        value: timeStep,
      }),
    );
  }

  onLayersVisibilityChange(layers: LayerItemV2[]): void {
    const layerVisibility: { [type: string]: boolean } = {};
    layers.forEach((layer) => {
      if (!layer.layerDefinition.isThematic) {
        layerVisibility[(layer.originalFiltrableProperties as CTMapFiltrableProperties).neType] =
          layer.isVisible;
      }
    });

    this.dispatchLayersVisibilityChange(layerVisibility);
  }

  dispatchLayersVisibilityChange(layers: { [type: string]: boolean }) {
    this._state.dispatch(
      new SetValueAction({
        fieldName: MasterOperatorFields.LayersVisibility,
        value: layers,
      }),
    );
  }

  getFrontendSettings() {}
}
